import { useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { createTicket } from "../../../api/ticketApi.js";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AddNewTickets.css";

/**
 * Create New Ticket Component
 * Updated for Auth Microservice integration - v2.0
 * User data comes from auth context (no manual name/email entry)
 */
const AddNewTickets = () => {
    const { user } = useAuth();
    let navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        subject: "",
        description: "",
        priority: "medium",
        category: "",
        issueFoundAt: new Date(),
        dueDate: null,
        tags: [],
    });
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    /**
     * Handle form field changes
     */
    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    /**
     * Handle file selection
     */
    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        
        // Validate file count (max 5 files)
        if (selectedFiles.length > 5) {
            toast.error("You can upload maximum 5 files.");
            return;
        }

        // Validate file sizes (max 50MB each)
        const maxSize = 50 * 1024 * 1024; // 50MB
        for (let file of selectedFiles) {
            if (file.size > maxSize) {
                toast.error(`File ${file.name} is too large. Maximum file size is 50MB.`);
                return;
            }
        }

        setFiles(selectedFiles);
        toast.success(`${selectedFiles.length} file(s) selected`);
    };

    /**
     * Remove a selected file
     */
    const removeFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        toast.success("File removed");
    };

    /**
     * Submit the ticket
     */
    const submitTicket = async () => {
        // Validation
        if (!formData.subject.trim()) {
            toast.error("Subject is required");
            return;
        }

        if (!formData.description.trim()) {
            toast.error("Description is required");
            return;
        }

        try {
            setSubmitting(true);

            // Create FormData for file upload
            const submitData = new FormData();
            submitData.append("subject", formData.subject);
            submitData.append("description", formData.description);
            submitData.append("priority", formData.priority);
            submitData.append("category", formData.category);
            submitData.append("issueFoundAt", formData.issueFoundAt.toISOString());
            
            if (formData.dueDate) {
                submitData.append("dueDate", formData.dueDate.toISOString());
            }

            if (formData.tags.length > 0) {
                submitData.append("tags", JSON.stringify(formData.tags));
            }

            // Append files
            files.forEach((file) => {
                submitData.append("attachments", file);
            });

            const response = await createTicket(submitData);

            if (response.success) {
                toast.success("Ticket created successfully! Check your email for confirmation.");
                
                // Navigate after short delay
                setTimeout(() => {
                    navigate("/my-tickets");
                }, 1500);
            } else {
                // Show specific error message from backend
                const errorMessage = response.message || "Failed to create ticket";
                const errorDetails = response.error || "";
                
                if (errorDetails.includes("unique") || errorDetails.includes("duplicate")) {
                    toast.error("Ticket number conflict. Please try again.");
                } else if (errorDetails) {
                    toast.error(`${errorMessage}: ${errorDetails}`);
                } else {
                    toast.error(errorMessage);
                }
            }
        } catch (error) {
            console.error("Error creating ticket:", error);
            
            // Handle network errors or other exceptions
            if (error.response?.data) {
                const errorData = error.response.data;
                const errorMessage = errorData.message || "Failed to create ticket";
                const errorDetails = errorData.error || "";
                
                if (errorDetails.includes("unique") || errorDetails.includes("duplicate")) {
                    toast.error("Ticket number conflict. Please try creating the ticket again.");
                } else if (errorDetails) {
                    toast.error(`${errorMessage}: ${errorDetails}`);
                } else {
                    toast.error(errorMessage);
                }
            } else {
                toast.error("Network error. Please check your connection and try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            background: "#ffffff",
            minHeight: "100vh",
            width: "100%"
        }}>
            <div style={{
                padding: "24px",
                width: "100%"
            }}>
                {/* Modern Header */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "32px",
                    paddingBottom: "24px",
                    borderBottom: "1px solid #e5e7eb"
                }}>
                    <h1 style={{
                        margin: 0,
                        fontSize: "24px",
                        fontWeight: "600",
                        color: "#111827",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                    }}>
                        Create New Ticket
                    </h1>
                    <button 
                        onClick={() => navigate(-1)}
                        style={{
                            background: "transparent",
                            color: "#4b5563",
                            border: "1px solid #d1d5db",
                            padding: "8px 16px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            transition: "all 0.15s"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f3f4f6";
                            e.currentTarget.style.borderColor = "#9ca3af";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "#d1d5db";
                        }}
                    >
                        <i className="las la-arrow-left" />
                        Back
                    </button>
                </div>

                {/* User Info Display - Minimal */}
                <div style={{ 
                    background: "#f9fafb", 
                    padding: "12px 16px", 
                    borderRadius: "8px", 
                    marginBottom: "24px",
                    border: "1px solid #e5e7eb",
                    fontSize: "13px",
                    color: "#6b7280"
                }}>
                    Creating as: <span style={{ fontWeight: "600", color: "#111827" }}>{user?.name}</span>
                </div>

                <div className="content" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

                    {/* Subject */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                            Subject <span style={{ color: "#dc2626" }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.subject}
                            onChange={(e) => handleChange("subject", e.target.value)}
                            placeholder="Brief summary of your issue"
                            maxLength={100}
                            style={{
                                padding: "12px 16px",
                                borderRadius: "8px",
                                border: "1px solid #d1d5db",
                                fontSize: "14px",
                                width: "100%",
                                transition: "border-color 0.15s"
                            }}
                            onFocus={(e) => e.target.style.borderColor = "#3bac75"}
                            onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                        />
                        <small style={{ color: "#6b7280", fontSize: "12px" }}>
                            {formData.subject.length}/100 characters
                        </small>
                    </div>

                    {/* Priority & Category Grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                                Priority <span style={{ color: "#dc2626" }}>*</span>
                            </label>
                            <select
                                value={formData.priority}
                                onChange={(e) => handleChange("priority", e.target.value)}
                                style={{
                                    padding: "12px 16px",
                                    borderRadius: "8px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "14px",
                                    width: "100%",
                                    background: "white",
                                    cursor: "pointer",
                                    transition: "border-color 0.15s"
                                }}
                                onFocus={(e) => e.target.style.borderColor = "#3bac75"}
                                onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                                Category
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => handleChange("category", e.target.value)}
                                style={{
                                    padding: "12px 16px",
                                    borderRadius: "8px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "14px",
                                    width: "100%",
                                    background: "white",
                                    cursor: "pointer",
                                    transition: "border-color 0.15s"
                                }}
                                onFocus={(e) => e.target.style.borderColor = "#3bac75"}
                                onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                            >
                                <option value="">Select category...</option>
                                <option value="Technical">Technical</option>
                                <option value="Billing">Billing</option>
                                <option value="General">General</option>
                                <option value="Compliance">Compliance</option>
                                <option value="ESG">ESG</option>
                                <option value="Account Issues">Account Issues</option>
                                <option value="Feature Request">Feature Request</option>
                                <option value="Bug Report">Bug Report</option>
                                <option value="Data Analytics">Data Analytics</option>
                                <option value="Integration">Integration</option>
                                <option value="Training">Training</option>
                                <option value="Security">Security</option>
                                <option value="Performance">Performance</option>
                                <option value="CRM">CRM</option>
                                <option value="LMS">LMS</option>
                                <option value="Survey Dashboard">Survey Dashboard</option>
                                <option value="BRSR">BRSR</option>
                                <option value="Compliance Tool">Compliance Tool</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    {/* Issue Found At & Due Date Grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                                Issue Found At
                            </label>
                            <DatePicker
                                selected={formData.issueFoundAt}
                                onChange={(date) => handleChange("issueFoundAt", date)}
                                dateFormat="MMM dd, yyyy"
                                wrapperClassName="date-picker-wrapper"
                                className="date-picker-input"
                            />
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                                Due Date <span style={{ color: "#6b7280", fontWeight: "400" }}>(Optional)</span>
                            </label>
                            <DatePicker
                                selected={formData.dueDate}
                                onChange={(date) => handleChange("dueDate", date)}
                                dateFormat="MMM dd, yyyy"
                                placeholderText="Select due date..."
                                isClearable
                                wrapperClassName="date-picker-wrapper"
                                className="date-picker-input"
                            />
                        </div>
                    </div>

                    {/* Description */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                            Description <span style={{ color: "#dc2626" }}>*</span>
                        </label>
                        <textarea
                            placeholder="Describe your issue in detail..."
                            value={formData.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            rows="6"
                            maxLength={1000}
                            style={{
                                padding: "12px 16px",
                                borderRadius: "8px",
                                border: "1px solid #d1d5db",
                                fontSize: "14px",
                                fontFamily: "inherit",
                                resize: "vertical",
                                width: "100%",
                                transition: "border-color 0.15s"
                            }}
                            onFocus={(e) => e.target.style.borderColor = "#3bac75"}
                            onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                        />
                        <small style={{ color: "#6b7280", fontSize: "12px" }}>
                            {formData.description.length}/1000 characters
                        </small>
                    </div>

                    {/* Attachments */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>
                            Attachments <span style={{ color: "#6b7280", fontWeight: "400" }}>(Optional)</span>
                        </label>
                        <div style={{
                            padding: "16px",
                            border: "2px dashed #d1d5db",
                            borderRadius: "8px",
                            background: "#f9fafb",
                            transition: "border-color 0.15s",
                            cursor: "pointer"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = "#3bac75"}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = "#d1d5db"}
                        >
                            <label style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                                <i className="las la-cloud-upload-alt" style={{ fontSize: "32px", color: "#9ca3af" }} />
                                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                                    Click to upload or drag and drop
                                </span>
                                <input
                                    type="file"
                                    multiple
                                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov,.avi,.mkv,.webm,.mp3,.wav,.m4v"
                                    onChange={handleFileChange}
                                    style={{ display: "none" }}
                                />
                            </label>
                        </div>
                        <small style={{ color: "#6b7280", fontSize: "12px" }}>
                            Max 5 files, 50MB each. Supported: images, PDFs, documents, videos
                        </small>
                        
                        {/* Display selected files */}
                        {files.length > 0 && (
                            <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                                {files.map((file, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "10px 14px",
                                            background: "#f3f4f6",
                                            borderRadius: "8px",
                                            border: "1px solid #e5e7eb"
                                        }}
                                    >
                                        <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#374151" }}>
                                            <i className="las la-file" style={{ color: "#3bac75" }} />
                                            {file.name} <span style={{ color: "#6b7280" }}>({(file.size / 1024).toFixed(2)} KB)</span>
                                        </span>
                                        <button
                                            onClick={() => removeFile(index)}
                                            style={{
                                                background: "transparent",
                                                color: "#dc2626",
                                                border: "1px solid #dc2626",
                                                padding: "6px 12px",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                                transition: "all 0.15s"
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = "#dc2626";
                                                e.currentTarget.style.color = "white";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "transparent";
                                                e.currentTarget.style.color = "#dc2626";
                                            }}
                                        >
                                            <i className="las la-times" /> Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Submit Button */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                    <button
                        onClick={submitTicket}
                        disabled={submitting}
                        style={{
                            padding: "12px 24px",
                            background: submitting ? "#9ca3af" : "#3bac75",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: submitting ? "not-allowed" : "pointer",
                            fontSize: "14px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            transition: "background 0.15s",
                            boxShadow: submitting ? "none" : "0 1px 3px rgba(0,0,0,0.1)"
                        }}
                        onMouseEnter={(e) => {
                            if (!submitting) e.currentTarget.style.background = "#2d8a5a";
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) e.currentTarget.style.background = "#3bac75";
                        }}
                    >
                        {submitting ? (
                            <>
                                <i className="las la-spinner la-spin" />
                                Creating Ticket...
                            </>
                        ) : (
                            <>
                                <i className="las la-plus-circle" />
                                Submit Ticket
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddNewTickets;
